import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Tooltip, Paper, Typography } from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as CenterIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';
import { Transformer } from 'markmap-lib';
import { Markmap } from 'markmap-view';
import '../../styles/markmap.css';

interface MindmapViewerProps {
  markdown: string;
  height?: number | string;
  onNodeClick?: (node: any) => void;
  metadata?: {
    generation_time?: number;
    node_count?: number;
  };
}

export const MindmapViewer: React.FC<MindmapViewerProps> = ({
  markdown,
  height = 400,
  onNodeClick,
  metadata,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const markmapRef = useRef<Markmap | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!svgRef.current || !markdown) return;

    try {
      // Transform markdown to markmap data
      const transformer = new Transformer();
      const { root } = transformer.transform(markdown);

      // Create or update markmap with proper configuration for connected nodes
      if (!markmapRef.current) {
        markmapRef.current = Markmap.create(svgRef.current, {
          duration: 500,
          maxWidth: 300,
          paddingX: 8,
          spacingVertical: 10,
          spacingHorizontal: 80,
          // Kotaemon-style vibrant colors for different depths
          color: (node: any) => {
            const colors = [
              '#FF6B35', // Orange for root
              '#4ECDC4', // Teal for level 1
              '#95E1D3', // Light teal for level 2
              '#F38181', // Pink for level 3
              '#AA96DA', // Purple for level 4
              '#FCBAD3', // Light pink for level 5
            ];
            return colors[node.depth % colors.length];
          },
          // Ensure links are visible and properly rendered
          nodeMinHeight: 16,
          lineHeight: 1.4,
          nodeFont: '14px sans-serif',
          // CRITICAL: These settings ensure links/connections are rendered
          autoFit: true,
          fitRatio: 0.95,
        });
      }

      // Set data and ensure proper rendering
      markmapRef.current.setData(root);
      
      // Force a re-render to ensure connections are drawn
      setTimeout(() => {
        if (markmapRef.current) {
          markmapRef.current.fit();
        }
      }, 100);

      // Add click handler if provided
      if (onNodeClick) {
        svgRef.current.addEventListener('click', (e) => {
          const target = e.target as HTMLElement;
          const nodeElement = target.closest('[data-depth]');
          if (nodeElement) {
            onNodeClick(nodeElement);
          }
        });
      }
    } catch (error) {
      console.error('Error rendering mindmap:', error);
    }

    return () => {
      if (markmapRef.current) {
        markmapRef.current.destroy();
        markmapRef.current = null;
      }
    };
  }, [markdown, onNodeClick]);

  const handleZoomIn = () => {
    if (markmapRef.current) {
      markmapRef.current.rescale(1.2);
    }
  };

  const handleZoomOut = () => {
    if (markmapRef.current) {
      markmapRef.current.rescale(0.8);
    }
  };

  const handleCenter = () => {
    if (markmapRef.current) {
      markmapRef.current.fit();
    }
  };

  const handleExport = () => {
    if (!svgRef.current) return;

    const svgData = new XMLSerializer().serializeToString(svgRef.current);
    const blob = new Blob([svgData], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'mindmap.svg';
    link.click();
    URL.revokeObjectURL(url);
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;

    if (!isFullscreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
    setIsFullscreen(!isFullscreen);
  };

  return (
    <Paper
      ref={containerRef}
      elevation={2}
      sx={{
        position: 'relative',
        height: isFullscreen ? '100vh' : height,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
            📊 Knowledge Map
          </Typography>
          {metadata?.generation_time && (
            <Typography variant="caption" color="text.secondary">
              ({metadata.generation_time.toFixed(2)}s)
            </Typography>
          )}
          {metadata?.node_count && (
            <Typography variant="caption" color="text.secondary">
              • {metadata.node_count} nodes
            </Typography>
          )}
        </Box>

        {/* Controls */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Zoom In">
            <IconButton size="small" onClick={handleZoomIn}>
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom Out">
            <IconButton size="small" onClick={handleZoomOut}>
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Center">
            <IconButton size="small" onClick={handleCenter}>
              <CenterIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export SVG">
            <IconButton size="small" onClick={handleExport}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
            <IconButton size="small" onClick={toggleFullscreen}>
              {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* SVG Container with dark background like Kotaemon */}
      <Box
        sx={{
          width: '100%',
          height: 'calc(100% - 56px)',
          overflow: 'hidden',
          bgcolor: '#2d3748', // Dark background like Kotaemon
          '& svg': {
            '& .markmap-node': {
              cursor: 'pointer',
            },
            '& .markmap-node-circle': {
              fill: '#fff',
              stroke: '#fff',
              strokeWidth: 1.5,
            },
            '& .markmap-node-text': {
              fill: '#fff',
              fontSize: '14px',
              fontFamily: 'sans-serif',
            },
            '& .markmap-link': {
              strokeWidth: 2,
            },
          },
        }}
      >
        <svg
          ref={svgRef}
          style={{
            width: '100%',
            height: '100%',
            cursor: 'grab',
            background: '#2d3748',
          }}
        />
      </Box>
    </Paper>
  );
};

export default MindmapViewer;
